<script setup lang="ts">
/**
 * SignDialog (Phase 12, frozen contract for Phase 13; UI-SPEC "Shared Contract:
 * SignDialog" + Amendment A-01).
 *
 * Shared sign dialog shell over the unmodified `MidFlowModal`. Flow wrappers
 * (InvoiceSignDialog now, the quote dialog in Phase 13) supply the `summary`,
 * `statement`, `extra`, and `success` slots and drive `submitting`,
 * `succeeded`, and `error`; this shell owns the pad, the error band, and the
 * single footer action per the UI-SPEC state table.
 *
 * Outcome precedence: succeeded > submitting > terminal error > retryable
 * error > form. So at most one band renders, and none while submitting.
 *
 * Error copy arrives as data (A-01). Body segments render by interpolation
 * only: strings as text, `{ text, href }` as a bound anchor. No raw HTML.
 *
 * Layout (Plan 12-13, 12-UAT-G1 / G2; the 2026-10-21 user decision overrides
 * D-09's pinned pad): the body flows as one column inside MidFlowModal's body
 * element, which is the only scroller. The single pad is the last element,
 * directly under the summary, statement, and `extra` slot, so nothing ever
 * renders below it; Submit stays pinned in MidFlowModal's footer and the
 * whitespace sits between the pad and Submit. There is no inner summary
 * scroller and no short-viewport height floor any more. When the column is
 * taller than the body (short phones, phone landscape), `revealPad()` scrolls
 * MidFlowModal's body element (never the window) on open and on every resize
 * of that element (rotation, browser chrome) so the pad is in view, using the
 * tiered math in `utils/pad-reveal`. The reveal never moves the body during a
 * stroke: a resize mid-stroke is deferred to the pad's next `update:inkState`.
 * It also stays out of the way while a text field in the body has focus, and
 * runs only in the form outcome. Phone landscape opts MidFlowModal into its
 * full-screen compact variant (its fullscreenWhenShort prop) so the label row,
 * canvas, hint, and Submit fit together at 303px tall.
 *
 * Privacy (D-19): the Datadog privacy attribute (hidden) and `data-sentry-block`
 * sit on the root and fall through MidFlowModal's `$attrs` to the teleported
 * backdrop, so the whole dialog is hidden from replay. The drawn PNG data URL goes straight
 * from `padRef.toPngDataUrl()` into the `submit` emit; it is never held in a
 * ref, prop, store, browser storage, or log call here.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useEventListener, useResizeObserver } from '@vueuse/core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
} from '@fortawesome/pro-solid-svg-icons';
import type { SignatureInkState } from '@sera/core';
import MidFlowModal from './MidFlowModal.vue';
import BaseButton from './BaseButton.vue';
import SignaturePad from './SignaturePad.vue';
import type { SignDialogError, SignDialogTextSegment } from '../types';
import { padRevealScrollTop } from '../utils/pad-reveal';

interface Props {
  visible: boolean;
  title: string;
  submitLabel?: string;
  submitting?: boolean;
  succeeded?: boolean;
  /** UI-SPEC Amendment A-01. Rendered as the retryable or terminal band. */
  error?: SignDialogError | null;
}

const props = withDefaults(defineProps<Props>(), {
  submitLabel: 'Submit signature',
  submitting: false,
  succeeded: false,
  error: null,
});

const emit = defineEmits<{
  'update:visible': [value: boolean];
  submit: [dataUrl: string];
}>();

defineSlots<{
  summary?: () => unknown;
  statement: () => unknown;
  extra?: () => unknown;
  success?: () => unknown;
}>();

type Outcome = 'form' | 'submitting' | 'retryable' | 'terminal' | 'success';

const padRef = ref<InstanceType<typeof SignaturePad> | null>(null);
const bodyRef = ref<HTMLElement | null>(null);
const actionRef = ref<InstanceType<typeof BaseButton> | null>(null);
const inkState = ref<SignatureInkState>('empty');

const outcome = computed<Outcome>(() => {
  if (props.succeeded) return 'success';
  if (props.submitting) return 'submitting';
  if (!props.error) return 'form';
  return props.error.retryable ? 'retryable' : 'terminal';
});

const bandError = computed<SignDialogError | null>(() =>
  outcome.value === 'retryable' || outcome.value === 'terminal'
    ? props.error
    : null,
);

const bodySegments = computed<ReadonlyArray<SignDialogTextSegment>>(() => {
  const body = bandError.value?.body ?? [];
  return typeof body === 'string' ? [body] : body;
});

const actionLabel = computed<string>(() =>
  outcome.value === 'retryable' ? 'Try again' : props.submitLabel,
);

/** MidFlowModal's body element: the dialog's only scroller. */
function scroller(): HTMLElement | null {
  return bodyRef.value?.parentElement ?? null;
}

// UI-SPEC: on any outcome, focus moves to the footer action and the body
// returns to the top so the band (or success body) is visible.
watch(outcome, async (next) => {
  if (next === 'form' || next === 'submitting') return;
  await nextTick();
  const action = actionRef.value?.$el as HTMLElement | undefined;
  action?.focus();
  const el = scroller();
  if (el) el.scrollTop = 0;
  // Not a customer scroll: a later return to the form may reveal again.
  padAnchorTop = null;
});

/** True when a text field inside the dialog body has focus (Phase 13 notes). */
function isEditingText(): boolean {
  const active = document.activeElement as HTMLElement | null;
  if (!active || !bodyRef.value?.contains(active)) return false;
  return active.matches('input, textarea, select') || active.isContentEditable;
}

/** The pad's well: the canvas's direct parent. */
function padWell(): HTMLElement | null {
  const padRoot = padRef.value?.$el as HTMLElement | undefined;
  return padRoot?.querySelector('canvas')?.parentElement ?? null;
}

/**
 * Review WR-04: the body scrollTop at which the customer was last "at the
 * pad" (set by every reveal and by any scroll that leaves the well fully in
 * view). A resize re-reveals only while the body is still there; once the
 * customer has scrolled away (to re-read the summary or attestation), a
 * resize leaves the body alone unless the orientation changed. `null` means
 * no anchor yet (just opened), which always reveals.
 */
let padAnchorTop: number | null = null;
/** Orientation seen at the last resize or open; a flip forces a reveal. */
let wasLandscape: boolean | null = null;

function isLandscape(): boolean {
  return window.innerWidth > window.innerHeight;
}

/** True when the well sits fully inside the body's visible box (1px slack). */
function wellInView(el: HTMLElement): boolean {
  const well = padWell();
  if (!well) return false;
  const top = el.getBoundingClientRect().top + el.clientTop;
  const box = well.getBoundingClientRect();
  return box.top >= top - 1 && box.bottom <= top + el.clientHeight + 1;
}

function scrolledAwayFromPad(el: HTMLElement): boolean {
  return padAnchorTop !== null && Math.abs(el.scrollTop - padAnchorTop) > 1;
}

function onScrollerScroll(): void {
  const el = scroller();
  if (el && wellInView(el)) padAnchorTop = el.scrollTop;
}

/**
 * Scrolls MidFlowModal's body so the pad is in view (12-UAT-G1 / G2). Only
 * in the form outcome; only ever assigns the body element's scrollTop. Tiers:
 * the whole pad, then label row + well, then the well alone. Every reveal
 * path (open, resize, deferred ink state, just-ended re-check) runs through
 * here, so the focused-text-field guard lives here too (review WR-03).
 */
function revealPad(): void {
  if (outcome.value !== 'form' || isEditingText()) return;
  const el = scroller();
  const padRoot = padRef.value?.$el as HTMLElement | undefined;
  const well = padWell();
  if (!el || !padRoot || !well) return;
  const viewTop = el.getBoundingClientRect().top + el.clientTop;
  const pad = padRoot.getBoundingClientRect();
  const wellBox = well.getBoundingClientRect();
  const next = padRevealScrollTop({
    view: { top: viewTop, bottom: viewTop + el.clientHeight },
    scrollTop: el.scrollTop,
    maxScrollTop: el.scrollHeight - el.clientHeight,
    tiers: [
      { top: pad.top, bottom: pad.bottom },
      { top: pad.top, bottom: wellBox.bottom },
      { top: wellBox.top, bottom: wellBox.bottom },
    ],
  });
  if (next !== null) el.scrollTop = next;
  padAnchorTop = el.scrollTop;
}

// A reveal requested while a stroke is active waits for the pad's next
// update:inkState (emitted at stroke end, never mid-stroke), so the canvas
// never moves under the finger (T-12-30).
let revealPending = false;

/**
 * The pad's guard also covers the instant right after a stroke ends (until a
 * 0ms timeout), whose update:inkState has already fired. A resize landing in
 * that window (lift the finger, rotate at once) would otherwise wait for the
 * next stroke, so re-check once the window has passed. A 0ms timer queued
 * now runs after the pad's own one. While a stroke is really active the
 * guard still holds and the reveal stays pending for update:inkState.
 */
function recheckPendingReveal(): void {
  if (!revealPending || padRef.value?.isStrokeActiveOrJustEnded()) return;
  revealPending = false;
  revealPad();
}

function onScrollerResize(): void {
  const landscape = isLandscape();
  const rotated = wasLandscape !== null && landscape !== wasLandscape;
  wasLandscape = landscape;
  if (outcome.value !== 'form') return;
  // WR-04: a deliberate scroll away from the pad survives URL-bar, keyboard,
  // and window resizes; only an orientation change re-reveals over it.
  const el = scroller();
  if (!rotated && el && scrolledAwayFromPad(el)) return;
  if (padRef.value?.isStrokeActiveOrJustEnded()) {
    revealPending = true;
    setTimeout(recheckPendingReveal, 0);
    return;
  }
  revealPad();
}

// Observes MidFlowModal's body on every open, rotation, and resize.
useResizeObserver(() => scroller(), onScrollerResize);
useEventListener(() => scroller(), 'scroll', onScrollerScroll, {
  passive: true,
});

function onInkState(state: SignatureInkState): void {
  inkState.value = state;
  // An ink-state emit means no stroke is active: run the deferred reveal.
  if (!revealPending) return;
  revealPending = false;
  revealPad();
}

// UI-SPEC "Phone (<640px)": block Chrome pull-to-refresh on <html> while the
// dialog is open, restoring whatever value was there before. MidFlowModal
// stays untouched. `null` means "not applied".
let prevOverscroll: string | null = null;

function restoreOverscroll(): void {
  if (prevOverscroll === null) return;
  document.documentElement.style.overscrollBehaviorY = prevOverscroll;
  prevOverscroll = null;
}

function applyOverscroll(): void {
  if (prevOverscroll !== null) return;
  const style = document.documentElement.style;
  prevOverscroll = style.overscrollBehaviorY;
  style.overscrollBehaviorY = 'contain';
}

watch(
  () => props.visible,
  async (isVisible) => {
    // Each open starts with no anchor, so the open-time reveal always runs.
    padAnchorTop = null;
    if (!isVisible) {
      revealPending = false;
      restoreOverscroll();
      return;
    }
    wasLandscape = isLandscape();
    // WR-02: the modal body (and so the pad) remounts empty on every open,
    // and a fresh pad emits no ink state until its first stroke. Without this
    // reset, a 'ready' cached from the last open enables Submit over an
    // empty pad.
    inkState.value = 'empty';
    applyOverscroll();
    // Reveal the pad once the body has rendered and laid out.
    await nextTick();
    requestAnimationFrame(revealPad);
  },
  { immediate: true },
);

onBeforeUnmount(restoreOverscroll);

// PAD-05: a pointer released over the backdrop after a stroke must not close
// the dialog. The pad's pointer capture (Plan 12-09) is the other half.
function onModalVisible(value: boolean): void {
  if (!value && padRef.value?.isStrokeActiveOrJustEnded()) return;
  emit('update:visible', value);
}

// Eligibility is recomputed from the pad at click time, never from the cached
// inkState (which only drives the disabled visual). One emit per click.
function onSubmit(): void {
  const pad = padRef.value;
  if (!pad || !pad.inkOk()) return;
  emit('submit', pad.toPngDataUrl());
}

function onDismiss(): void {
  emit('update:visible', false);
}
</script>

<template>
  <MidFlowModal
    :visible="visible"
    :title="title"
    size="md"
    :closable="!submitting"
    fullscreen-when-short
    contain-overscroll
    data-testid="sign-dialog"
    data-dd-privacy="hidden"
    data-sentry-block
    @update:visible="onModalVisible"
  >
    <span class="sr-only" aria-live="polite">{{
      submitting ? 'Submitting your signature.' : ''
    }}</span>
    <!-- Flow layout (Plan 12-13; the 2026-10-21 user decision overrides
         D-09's pinned pad): one column inside MidFlowModal's body, which is
         the only scroller. The pad zone is the last child, directly under
         the summary, statement, and extra slot; Submit stays pinned in the
         footer. No height, flex, or overflow classes here on purpose. -->
    <div ref="bodyRef" data-testid="sign-dialog-body">
      <div v-if="succeeded" role="status" data-testid="sign-dialog-success">
        <slot name="success">
          <div class="flex flex-col items-center py-6 text-center">
            <span
              class="mb-4 inline-flex size-14 items-center justify-center rounded-full bg-success-bg text-success"
              aria-hidden="true"
            >
              <FontAwesomeIcon :icon="faCircleCheck" class="text-2xl" />
            </span>
            <p class="text-lg/tight font-semibold text-foreground">
              Signature saved
            </p>
          </div>
        </slot>
      </div>
      <template v-else>
        <div
          v-if="bandError"
          role="alert"
          data-testid="sign-dialog-error"
          :data-kind="bandError.kind"
          :class="[
            'mb-4 flex items-start gap-2 rounded-md px-4 py-2',
            outcome === 'terminal'
              ? 'border border-border-strong bg-canvas'
              : 'border border-danger/30 bg-danger-bg',
          ]"
        >
          <FontAwesomeIcon
            v-if="outcome === 'terminal'"
            :icon="faCircleInfo"
            class="mt-0.5 size-4 shrink-0 text-muted"
            aria-hidden="true"
          />
          <FontAwesomeIcon
            v-else
            :icon="faCircleExclamation"
            class="mt-0.5 size-4 shrink-0 text-danger"
            aria-hidden="true"
          />
          <div class="min-w-0">
            <p class="text-sm font-semibold text-foreground">
              {{ bandError.heading }}
            </p>
            <p class="mt-1 text-sm text-foreground">
              <template v-for="(segment, i) in bodySegments" :key="i">
                <template v-if="typeof segment === 'string'">{{
                  segment
                }}</template>
                <a
                  v-else
                  :href="segment.href"
                  class="font-semibold text-foreground underline"
                  >{{ segment.text }}</a
                >
              </template>
            </p>
          </div>
        </div>
        <slot name="summary" />
        <slot name="statement" />
        <slot name="extra" />
      </template>
      <!-- Pad zone: the single pad instance, always the last child. On short
           viewports (fullscreenWhenShort makes the panel full width) the pad
           is capped at the 480px md card's 432px content width and centred
           (review WR-01): the ink gate's width-relative bar is 15% of the
           canvas width, so a full-width landscape canvas (about 814px at
           864x303) would turn a valid portrait signature "insufficient"
           after rotation. The divider keeps the full body width. -->
      <div v-if="!succeeded" class="mt-4 border-t border-border pt-4">
        <SignaturePad
          ref="padRef"
          class="[@media(max-height:480px)]:mx-auto [@media(max-height:480px)]:max-w-[432px]"
          :disabled="submitting || outcome === 'terminal'"
          @update:ink-state="onInkState"
        />
      </div>
    </div>

    <template #footer>
      <BaseButton
        v-if="outcome === 'success'"
        ref="actionRef"
        variant="secondary"
        label="Done"
        data-testid="sign-dialog-done"
        class="w-full sm:w-auto"
        @click="onDismiss"
      />
      <BaseButton
        v-else-if="outcome === 'terminal'"
        ref="actionRef"
        variant="secondary"
        label="Close"
        data-testid="sign-dialog-close"
        class="w-full sm:w-auto"
        @click="onDismiss"
      />
      <BaseButton
        v-else
        ref="actionRef"
        variant="primary"
        :label="actionLabel"
        :loading="submitting"
        :disabled="inkState !== 'ready'"
        data-testid="sign-dialog-submit"
        class="w-full bg-cta text-cta-text sm:w-auto"
        @click="onSubmit"
      />
    </template>
  </MidFlowModal>
</template>
